// src/pages/SettingsMulti.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import api from "../auth/api";
import BotsPanel from "../components/bots/BotsPanel";
import ChannelBotSwitcher from "../components/ChannelBotSwitcher";
import { QRCodeCanvas } from "qrcode.react";

export default function SettingsMulti() {
  const noCache = {
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  };
  const [accounts, setAccounts] = useState([]); // [{id,label,phone,provider,status,...}]
  const [connectedMap, setConnectedMap] = useState({}); // { [id]: boolean }
  const [qrMap, setQrMap] = useState({}); // { [id]: string }
  const [qrStatusMap, setQrStatusMap] = useState({}); // { [id]: { state,lastCode,lastError } }
  const pollRef = useRef({ status: null, qr: null });

  // ─── NUEVO: mapa de conexiones por canal y bots disponibles ───────────────
  const [connectionsMap, setConnectionsMap] = useState({}); // { [channel_id]: [connections...] }
  const [bots, setBots] = useState([]); // [{id,name,...}]
  const [connBotMap, setConnBotMap] = useState({}); // { `${channelId}:${connId}`: {bot_id,is_active} }

  // --- Crear canal (UI state)
  const [createOpen, setCreateOpen] = useState(false);
  const [provider, setProvider] = useState("baileys"); // 'baileys' | 'cloud'
  const [label, setLabel] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [metaToken, setMetaToken] = useState("");
  const [apiVersion, setApiVersion] = useState("v20.0");
  const [busy, setBusy] = useState(false);

  // --- Mail settings (SMTP)
  const [mailLoading, setMailLoading] = useState(false);
  const [mailSaving, setMailSaving] = useState(false);
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [secureTLS, setSecureTLS] = useState(false); // boolean
  const [mailMsg, setMailMsg] = useState(""); // feedback corto
  const [showPass, setShowPass] = useState(false);

  // Helpers de endpoint con fallback y normalización
  const ep = useMemo(() => {
    const noCache = {
      headers: {
        "Cache-Control": "no-store", // más agresivo
        Pragma: "no-cache",
        "If-Modified-Since": "0",
      },
    };

    const bust = () => `?_=${Date.now()}`;

    const req = {
      get: (url) => api.get(`${url}${bust()}`, noCache),
      post: (url, body) => api.post(url + bust(), body, noCache),
      del: (url) => api.delete(url + bust(), noCache),
    };

    const normalizeConnected = (data) => {
      if (!data || typeof data !== "object") return false;

      // 1) Campos booleanos típicos
      for (const k of ["connected", "isConnected", "online", "is_online"]) {
        if (typeof data[k] === "boolean") return !!data[k];
      }

      // 2) Strings “open/connected/ready/…” o “close/offline/…”
      for (const k of [
        "connected",
        "isConnected",
        "online",
        "is_online",
        "status",
        "state",
        "connection",
      ]) {
        const v = data[k];
        if (typeof v === "string") {
          const t = v.toLowerCase();
          if (
            [
              "true",
              "ok",
              "open",
              "opened",
              "connected",
              "online",
              "ready",
              "authenticated",
            ].includes(t)
          )
            return true;
          if (
            [
              "false",
              "close",
              "closed",
              "disconnected",
              "offline",
              "connecting",
              "init",
              "pairing",
            ].includes(t)
          )
            return false;
        }
      }

      // 3) Objetos de estado tipo Baileys: { state: { connection: 'open' } }
      if (data.state && typeof data.state === "object") {
        const c =
          data.state.connection ||
          data.state.status ||
          "";
        const lc = c.toLowerCase?.() || "";
        if (["open", "connected", "ready", "authenticated"].includes(lc))
          return true;
        if (["close", "closed", "disconnected", "offline"].includes(lc))
          return false;
      }

      // 4) Señales de sesión activa
      if (data.user || data.me || data.jid) return true;

      // 5) Pairing info
      if (data.pairing && typeof data.pairing === "object") {
        const c = String(
          data.pairing.status || data.pairing.state || ""
        ).toLowerCase();
        if (
          ["open", "connected", "ready", "paired", "authenticated"].includes(c)
        )
          return true;
      }

      // 6) Sockets
      const rs = data?.ws?.readyState ?? data?.socket?.readyState;
      if (typeof rs === "number") return rs === 1;

      // 7) Números “truthy”
      for (const k of ["connected", "isConnected", "status", "state", "connection"]) {
        const v = data[k];
        if (typeof v === "number") {
          if (v === 1 || (v >= 200 && v < 300)) return true;
        }
      }

      return false;
    };

    const normalizeQR = (data) =>
      data?.qr ??
      data?.qrcode ??
      data?.qrCode ??
      data?.currentQR ??
      data?.qrBase64 ??
      data?.pairing?.qr ??
      data?.data?.qr ??
      data?.state?.qr ??
      "";

    return {
      // Lista de cuentas: intenta usar /wa/accounts-with-state primero
      list: async () => {
        // intento con /wa/accounts-with-state
        try {
          const { data } = await req.get(`/wa/accounts-with-state`);
          if (data?.rows) return data.rows;
        } catch (e) {
          // fallback a /wa/accounts normal
        }

        try {
          const { data } = await req.get(`/wa/accounts`);
          if (data?.rows) return data.rows;
        } catch (e) {
          console.error("ep.list error:", e?.response?.data || e);
        }

        // Fallback single-session
        return [
          {
            id: "default",
            label: "Baileys Web Default",
            phone: "baileys:default",
            provider: "baileys",
            status: "unknown",
          },
        ];
      },

      status: async (acc) => {
        try {
          const { data } = await req.get(`/wa/accounts/${acc.id}/status`);
          return { connected: normalizeConnected(data), raw: data };
        } catch (e) {
          console.error("ERROR en ep.status:", e?.response?.data || e);
          return { connected: false, raw: null };
        }
      },

      getQR: async (acc) => {
        if (acc.provider !== "baileys") return { qr: "" };
        const path = `/wa/accounts/${acc.id}/qr`;
        try {
          const { data } = await req.get(path);
          const qr = normalizeQR(data);
          console.log("ep.getQR qrBase64", acc.id, qr);
          return {
            qr: qr || "",
            state: data?.state || "",
            lastCode: data?.lastCode ?? null,
            lastError: data?.lastError || "",
          };
        } catch (e) {
          console.error("ERROR en ep.getQR:", e?.response?.data || e);
          return {
            qr: "",
            state: "error",
            lastCode: e?.response?.status ?? null,
            lastError: e?.response?.data?.error || e?.message || "Error consultando QR",
          };
        }
      },

      connect: async (acc) => {
        const { data } = await req.post(`/wa/accounts/${acc.id}/connect`, null);
        return data;
      },

      reset: async (acc) => {
        try {
          await req.post(`/wa/accounts/${acc.id}/reset`, null);
        } catch {
          await req.post(`/session/reset`, null).catch(() => {});
        }
      },

      create: async (payload) => {
        try {
          const { data } = await req.post(`/wa/accounts`, payload);
          return data; // { id, ... }
        } catch (e) {
          console.error("ERROR en ep.create:", e?.response?.data || e);
          throw e;
        }
      },

      remove: async (acc) => {
        await req.del(`/wa/accounts/${acc.id}`);
      },

      // ─── NUEVO: conexiones por canal ─────────────────────────────
      listConnections: async (channelId) => {
        if (!channelId) return [];
        const { data } = await req.get(`/channels/${channelId}/connections`);
        return data?.rows || data?.connections || [];
      },
      // Bots del sistema (para selector)
      listBots: async () => {
        const { data } = await req.get(`/bots`);
        return data?.rows || data?.bots || [];
      },
      getConnectionBot: async (channelId, connId) => {
        const { data } = await req.get(
          `/channels/${channelId}/connections/${connId}/bot`
        );
        return { bot_id: data?.bot_id ?? null, is_active: !!data?.is_active };
      },
      setConnectionBot: async (channelId, connId, payload) => {
        const { data } = await req.post(
          `/channels/${channelId}/connections/${connId}/bot`,
          payload
        );
        return data;
      },
    };
  }, []);

  const ConnectionBotRow = ({ channelId, conn }) => {
    const key = `${channelId}:${conn.id}`;
    const cur = connBotMap[key] || { bot_id: null, is_active: 0 };
    const [saving, setSaving] = useState(false);

    const onSave = async (patch) => {
      setSaving(true);
      try {
        const payload = {
          bot_id: patch.bot_id ?? cur.bot_id,
          is_active: (patch.is_active ?? cur.is_active) ? 1 : 0,
        };
        await ep.setConnectionBot(channelId, conn.id, payload);
        setConnBotMap((m) => ({
          ...m,
          [key]: { bot_id: payload.bot_id, is_active: !!payload.is_active },
        }));
      } catch (e) {
        alert(
          e?.response?.data?.error ||
            "No se pudo guardar el bot de la conexión."
        );
      } finally {
        setSaving(false);
      }
    };

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto",
          gap: 8,
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>
            {conn.name ||
              conn.label ||
              `${conn.provider_code || conn.provider}`}
          </div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {conn.provider_code || conn.provider} ·{" "}
            {conn.identifier ||
              conn.phone_number_id ||
              conn.device_id ||
              conn.id}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            className="input"
            value={cur.bot_id || ""}
            onChange={(e) =>
              onSave({ bot_id: Number(e.target.value) || null })
            }
            disabled={saving}
          >
            <option value="">— Sin bot (usa bot del canal) —</option>
            {bots.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name || `Bot #${b.id}`}
              </option>
            ))}
          </select>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
            }}
          >
            <input
              type="checkbox"
              checked={!!cur.is_active}
              onChange={(e) => onSave({ is_active: e.target.checked })}
              disabled={saving}
            />
            Activo
          </label>
        </div>
        <div>
          <button
            className="btn btn-muted"
            onClick={() => onSave({})}
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    );
  };

  // Cargar cuentas
  const fetchAccounts = async () => {
    try {
      const rows = await ep.list();
      setAccounts(rows);

      // si /wa/accounts-with-state trae isConnected/currentQR, úsalo para inicializar
      const nextConnected = {};
      const nextQR = {};
      for (const acc of rows) {
        if (typeof acc.isConnected === "boolean") {
          nextConnected[acc.id] = !!acc.isConnected;
        }
        if (acc.currentQR) {
          nextQR[acc.id] = acc.currentQR;
        }
      }
      if (Object.keys(nextConnected).length) {
        setConnectedMap((m) => ({ ...m, ...nextConnected }));
      }
      if (Object.keys(nextQR).length) {
        setQrMap((m) => ({ ...m, ...nextQR }));
      }
    } catch {
      setAccounts([
        {
          id: "default",
          label: "Baileys Web Default",
          phone: "baileys:default",
          provider: "baileys",
          status: "unknown",
        },
      ]);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const list = await ep.listBots();
        setBots(list);
      } catch {}
    })();
  }, [ep]);

  // Cargar conexiones por cada cuenta que tenga channel_id
  useEffect(() => {
    (async () => {
      const next = {};
      const nextConnBot = {};
      for (const acc of accounts) {
        const channelId = acc.channel_id || acc.channelId;
        if (!channelId) continue;
        try {
          const conns = await ep.listConnections(channelId);
          next[channelId] = conns;
          for (const c of conns) {
            try {
              const st = await ep.getConnectionBot(channelId, c.id);
              nextConnBot[`${channelId}:${c.id}`] = st;
            } catch {}
          }
        } catch {}
      }
      setConnectionsMap(next);
      setConnBotMap((m) => ({ ...m, ...nextConnBot }));
    })();
  }, [accounts, ep]);

  const loadMailSettings = async () => {
    setMailMsg("");
    setMailLoading(true);
    try {
      const { data } = await api.get(
        `/mail/settings?_=${Date.now()}`,
        noCache
      );
      const s = data?.settings || {};
      setSmtpHost(s.smtp_host || "");
      setSmtpPort(Number(s.smtp_port) || 587);
      setSmtpUser(s.smtp_user || "");
      setSmtpPass(s.smtp_pass || "");
      setFromEmail(s.from_email || "");
      setSecureTLS(!!s.secure);
    } catch (e) {
      setMailMsg(
        e?.response?.data?.error ||
          "No se pudo cargar la configuración de correo."
      );
    } finally {
      setMailLoading(false);
    }
  };

  const saveMailSettings = async () => {
    setMailMsg("");
    if (
      !smtpHost.trim() ||
      !smtpUser.trim() ||
      !smtpPass.trim() ||
      !fromEmail.trim()
    ) {
      setMailMsg("Por favor completa los campos obligatorios.");
      return;
    }

    const isEmailLike = (s) =>
      /<?[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,}>?$/.test(s);
    if (!isEmailLike(fromEmail.trim())) {
      setMailMsg("FROM no parece un correo válido.");
      return;
    }

    const port = Number(smtpPort);
    if (!Number.isInteger(port) || port <= 0) {
      setMailMsg("El puerto SMTP no es válido.");
      return;
    }

    setMailSaving(true);
    try {
      const payload = {
        smtp_host: smtpHost.trim(),
        smtp_port: port,
        smtp_user: smtpUser.trim(),
        smtp_pass: smtpPass,
        from_email: fromEmail.trim(),
        secure: !!secureTLS,
      };
      const { data } = await api.post(`/mail/settings`, payload);
      setMailMsg(data?.message || "Configuración guardada.");
    } catch (e) {
      setMailMsg(
        e?.response?.data?.error || "Error guardando configuración."
      );
    } finally {
      setMailSaving(false);
    }
  };

  useEffect(() => {
    loadMailSettings();
  }, []);
  useEffect(() => {
    fetchAccounts();
  }, []);

  // Poll de status (cada 2.5s)
  useEffect(() => {
    if (pollRef.current.status) clearInterval(pollRef.current.status);
    pollRef.current.status = setInterval(async () => {
      if (!accounts.length) return;
      for (const acc of accounts) {
        try {
          const { connected } = await ep.status(acc);
          setConnectedMap((m) => ({ ...m, [acc.id]: !!connected }));
        } catch {}
      }
    }, 2500);
    return () => {
      if (pollRef.current.status) clearInterval(pollRef.current.status);
      pollRef.current.status = null;
    };
  }, [accounts, ep]);

  // Poll de QR (cada 2.5s)
  useEffect(() => {
    if (pollRef.current.qr) clearInterval(pollRef.current.qr);
    pollRef.current.qr = setInterval(async () => {
      for (const acc of accounts) {
        if (acc.provider !== "baileys") continue;
        if (connectedMap[acc.id]) {
          setQrMap((m) => ({ ...m, [acc.id]: "" }));
          continue;
        }
        try {
          const qrState = await ep.getQR(acc);
          const { qr } = qrState;
          console.log("useEffect QR recibido para acc", acc.id, qr);
          setQrMap((m) => ({ ...m, [acc.id]: qr || "" }));
          setQrStatusMap((m) => ({ ...m, [acc.id]: qrState }));
        } catch {}
      }
    }, 2500);
    return () => {
      if (pollRef.current.qr) clearInterval(pollRef.current.qr);
      pollRef.current.qr = null;
    };
  }, [accounts, connectedMap, ep]);

  // Acciones
  const handleReset = async (acc) => {
    if (
      !window.confirm(
        `Esto cerrará la sesión de "${acc.label}" y pedirá un nuevo QR. ¿Continuar?`
      )
    )
      return;
    try {
      await ep.reset(acc);
      const qrState = await ep.getQR(acc);
      const { qr } = qrState;
      console.log("handleReset QR recibido para acc", acc.id, qr);
      setQrMap((m) => ({ ...m, [acc.id]: qr || "" }));
      setQrStatusMap((m) => ({ ...m, [acc.id]: qrState }));
      setConnectedMap((m) => ({ ...m, [acc.id]: false }));
      alert(`Sesión reiniciada para "${acc.label}". Escanea el nuevo QR.`);
    } catch (e) {
      alert(
        e?.response?.data?.error || "No se pudo resetear la sesión."
      );
    }
  };

  const handleConnect = async (acc) => {
    try {
      await ep.connect(acc);
      // puede que aún no haya QR; el poll lo irá trayendo
      const qrState = await ep.getQR(acc);
      const { qr } = qrState;
      console.log("handleConnect QR recibido para acc", acc.id, qr);
      setQrMap((m) => ({ ...m, [acc.id]: qr || "" }));
      setQrStatusMap((m) => ({ ...m, [acc.id]: qrState }));
      setConnectedMap((m) => ({ ...m, [acc.id]: false }));
    } catch (e) {
      console.error(
        "connect error",
        e?.response?.data || e?.message || e
      );
      alert(
        e?.response?.data?.error ||
          e?.message ||
          "No se pudo iniciar la conexión"
      );
    }
  };

  const handleRemove = async (acc) => {
    if (!window.confirm(`¿Eliminar el canal "${acc.label}"?`)) return;
    try {
      await ep.remove(acc);
      await fetchAccounts();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo eliminar.");
    }
  };

  const handleCreate = async () => {
    if (!label.trim()) {
      alert("Ingresa un nombre (label)");
      return;
    }
    setBusy(true);
    try {
      const payload =
        provider === "cloud"
          ? {
              label: label.trim(),
              provider: "cloud",
              phone_number_id: phoneNumberId.trim(),
              waba_id: wabaId.trim(),
              meta_token: metaToken.trim(),
              api_version: apiVersion.trim() || "v20.0",
            }
          : { label: label.trim(), provider: "baileys" };

      const res = await ep.create(payload);

      setLabel("");
      setPhoneNumberId("");
      setWabaId("");
      setMetaToken("");
      setApiVersion("v20.0");
      setCreateOpen(false);

      const newList = await ep.list();
      setAccounts(newList);

      if (payload.provider === "baileys") {
        const newId = res?.id;
        const acc =
          newId != null
            ? { id: newId, provider: "baileys" }
            : newList.find(
                (a) =>
                  a.label === payload.label && a.provider === "baileys"
              );

        if (acc?.id) {
          try {
            await ep.connect(acc);
            const qrState = await ep.getQR(acc);
            const { qr } = qrState;
            console.log("handleCreate QR recibido para acc", acc.id, qr);
            setQrMap((m) => ({ ...m, [acc.id]: qr || "" }));
            setQrStatusMap((m) => ({ ...m, [acc.id]: qrState }));
            setConnectedMap((m) => ({ ...m, [acc.id]: false }));
          } catch {}
        }
      }
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo crear el canal.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-title">Ajustes</div>

      <section className="card section" style={{ maxWidth: 980 }}>
        <h3 style={{ marginTop: 0 }}>Sesiones / Canales de WhatsApp</h3>

        {/* CREAR CANAL */}
        <div className="card" style={{ padding: 14, marginBottom: 16 }}>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              className="btn"
              onClick={() => setCreateOpen((v) => !v)}
            >
              {createOpen ? "Cerrar" : "Nuevo canal"}
            </button>
            <div className="text-xs text-[color:var(--muted)]">
              Crea conexiones: Baileys (QR) o Cloud (sin QR).
            </div>
          </div>

          {createOpen && (
            <div
              className="grid gap-3 mt-3"
              style={{
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(240px,1fr))",
              }}
            >
              <div>
                <label className="font-semibold block mb-1">
                  Nombre (label)
                </label>
                <input
                  className="input"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Ventas GYE / Soporte UIO"
                />
              </div>

              <div>
                <label className="font-semibold block mb-1">
                  Proveedor
                </label>
                <select
                  className="input"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                >
                  <option value="baileys">Baileys (QR)</option>
                  <option value="cloud">Cloud API (Meta)</option>
                </select>
              </div>

              {provider === "cloud" && (
                <>
                  <div>
                    <label className="font-semibold block mb-1">
                      PHONE_NUMBER_ID
                    </label>
                    <input
                      className="input"
                      value={phoneNumberId}
                      onChange={(e) =>
                        setPhoneNumberId(e.target.value)
                      }
                      placeholder="123456789012345"
                    />
                  </div>
                  <div>
                    <label className="font-semibold block mb-1">
                      WABA_ID
                    </label>
                    <input
                      className="input"
                      value={wabaId}
                      onChange={(e) => setWabaId(e.target.value)}
                      placeholder="WhatsApp Business Account ID"
                    />
                  </div>
                  <div>
                    <label className="font-semibold block mb-1">
                      META_TOKEN
                    </label>
                    <input
                      className="input"
                      value={metaToken}
                      onChange={(e) => setMetaToken(e.target.value)}
                      placeholder="EAAG..."
                    />
                  </div>
                  <div>
                    <label className="font-semibold block mb-1">
                      API_VERSION
                    </label>
                    <input
                      className="input"
                      value={apiVersion}
                      onChange={(e) =>
                        setApiVersion(e.target.value)
                      }
                      placeholder="v20.0"
                    />
                  </div>
                </>
              )}

              <div className="flex items-end gap-2">
                <button
                  className="btn"
                  onClick={handleCreate}
                  disabled={busy}
                >
                  Crear canal
                </button>
                <button
                  className="btn btn-muted"
                  onClick={() => {
                    setCreateOpen(false);
                    setLabel("");
                    setPhoneNumberId("");
                    setMetaToken("");
                    setApiVersion("v20.0");
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        {accounts.length === 0 && (
          <div className="muted">No hay canales configurados.</div>
        )}

        {/* Leyenda estado */}
        <div className="flex gap-3 items-center my-2">
          <span className="inline-flex items-center gap-1 text-xs opacity-80">
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#22c55e",
                border: "2px solid #0f172a",
              }}
            />
            Conectado
          </span>
          <span className="inline-flex items-center gap-1 text-xs opacity-80">
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#ef4444",
                border: "2px solid #0f172a",
              }}
            />
            Desconectado
          </span>
        </div>

        {/* GRID de canales */}
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns:
              "repeat(auto-fill, minmax(360px, 1fr))",
          }}
        >
          {accounts.map((acc) => {
            const connected = Boolean(connectedMap[acc.id]);
            const qr = qrMap[acc.id] || "";
            const qrStatus = qrStatusMap[acc.id] || {};
            const isBaileys = acc.provider === "baileys";
            const dotColor = connected ? "#22c55e" : "#ef4444";
            const channelId = acc.channel_id ?? acc.channelId;
            const channelConns = channelId
              ? connectionsMap[channelId] || []
              : [];

            return (
              <div key={acc.id} className="card" style={{ padding: 16 }}>
                {/* Cabecera */}
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: "1fr auto",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        title={
                          connected
                            ? "Conectado"
                            : "Desconectado"
                        }
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          background: dotColor,
                          border: "2px solid #0f172a",
                          flex: "0 0 auto",
                        }}
                      />
                      <div className="font-bold text-base">
                        {acc.label}
                      </div>
                    </div>
                    <div className="text-xs opacity-80 flex flex-wrap gap-2 mt-1">
                      <span>
                        {connected
                          ? "Conectado"
                          : "Desconectado"}
                      </span>
                      <span>·</span>
                      <span>
                        {isBaileys ? "Baileys" : "Cloud API"}
                      </span>
                      {acc.phone && (
                        <>
                          <span>·</span>
                          <span title="Identificador lógico del canal">
                            {acc.phone}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap justify-end">
                    <button
                      className="btn btn-muted"
                      onClick={() => handleConnect(acc)}
                      title={
                        isBaileys
                          ? "Forzar reconexión / iniciar QR"
                          : "Consultar estado"
                      }
                    >
                      {isBaileys
                        ? "Forzar reconexión"
                        : "Consultar estado"}
                    </button>

                    {isBaileys && (
                      <button
                        className="btn btn-warn"
                        onClick={() => handleReset(acc)}
                      >
                        Cambiar de celular (reset)
                      </button>
                    )}

                    <button
                      className="btn btn-warn"
                      onClick={() => handleRemove(acc)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>

                {/* Conexiones del canal */}
                <div className="mt-3">
                  {channelId && channelConns.length > 0 && (
                    <div
                      className="card mt-3"
                      style={{ padding: 12 }}
                    >
                      <div className="font-bold mb-2">
                        Conexiones del canal
                      </div>
                      <div className="grid gap-2">
                        {channelConns.map((conn) => (
                          <ConnectionBotRow
                            key={conn.id}
                            channelId={channelId}
                            conn={conn}
                          />
                        ))}
                      </div>
                      <div className="text-xs opacity-70 mt-2">
                        Si una conexión no tiene bot activo, se
                        usará el bot asignado <b>al canal</b>{" "}
                        (fallback).
                      </div>
                    </div>
                  )}
                </div>

                {/* QR (solo Baileys y desconectado) */}
                {isBaileys && !connected && (
                  <div className="mt-3 text-center">
                    <div className="mb-2 text-[color:var(--muted)]">
                      {qr
                        ? `Escanea este QR para "${acc.label}"`
                        : "Esperando QR..."}
                    </div>

                    {qr ? (
                      <QRCodeCanvas
                        value={qr.trim()}
                        size={320}
                        level="L"
                        includeMargin={true}
                      />
                    ) : (
                      <div className="text-xs opacity-70">
                        {qrStatus.lastError
                          ? `Estado ${qrStatus.state || "desconocido"}: ${qrStatus.lastError}`
                          : `Aún no hay QR del servidor${qrStatus.state ? ` (${qrStatus.state})` : ""}`}
                      </div>
                    )}
                  </div>
                )}

                {!isBaileys && (
                  <div className="mt-2 text-xs opacity-80">
                    Este canal no usa QR (WhatsApp Cloud API).
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Marca */}
      <section className="card section" style={{ maxWidth: 720 }}>
        <h3 style={{ marginTop: 0 }}>Marca</h3>
        <label>
          Logo (URL){" "}
          <input
            className="input"
            placeholder="https://tusitio.com/logo.png"
          />
        </label>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>
          Puedes implementar guardado real si lo necesitas.
        </div>
      </section>

      {/* Correo (SMTP) */}
      <section className="card section" style={{ maxWidth: 720 }}>
        <h3 style={{ marginTop: 0 }}>Correo (SMTP)</h3>

        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns:
              "repeat(auto-fit, minmax(240px,1fr))",
          }}
        >
          <div>
            <label
              style={{
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              SMTP Host *
            </label>
            <input
              className="input"
              placeholder="smtp.tuservidor.com"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              disabled={mailLoading || mailSaving}
            />
          </div>

          <div>
            <label
              style={{
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              SMTP Port *
            </label>
            <input
              className="input"
              type="number"
              min={1}
              placeholder="587"
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
              disabled={mailLoading || mailSaving}
            />
          </div>

          <div>
            <label
              style={{
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Usuario (SMTP User) *
            </label>
            <input
              className="input"
              placeholder="usuario@tudominio.com"
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              disabled={mailLoading || mailSaving}
            />
          </div>

          <div>
            <label
              style={{
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Contraseña (SMTP Pass) *
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="input"
                type={showPass ? "text" : "password"}
                placeholder="••••••••"
                value={smtpPass}
                onChange={(e) => setSmtpPass(e.target.value)}
                disabled={mailLoading || mailSaving}
              />
              <button
                type="button"
                className="btn btn-muted"
                onClick={() => setShowPass((v) => !v)}
                disabled={mailLoading || mailSaving}
                title={showPass ? "Ocultar" : "Mostrar"}
              >
                {showPass ? "Ocultar" : "Ver"}
              </button>
            </div>
          </div>

          <div>
            <label
              style={{
                fontWeight: 600,
                display: "block",
                marginBottom: 6,
              }}
            >
              Remitente (FROM) *
            </label>
            <input
              className="input"
              placeholder="Soporte <soporte@tudominio.com>"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              disabled={mailLoading || mailSaving}
            />
            <div style={{ fontSize: 12, color: "var(--muted)" }}>
              Acepta “correo@dominio.com” o “Nombre &lt;correo@dominio.com&gt;”.
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "end",
              gap: 8,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <input
                type="checkbox"
                checked={secureTLS}
                onChange={(e) => setSecureTLS(e.target.checked)}
                disabled={mailLoading || mailSaving}
              />
              Usar conexión segura (TLS/SSL)
            </label>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            className="btn"
            onClick={saveMailSettings}
            disabled={mailLoading || mailSaving}
          >
            {mailSaving ? "Guardando..." : "Guardar configuración"}
          </button>
          <button
            className="btn btn-muted"
            onClick={loadMailSettings}
            disabled={mailLoading || mailSaving}
          >
            {mailLoading ? "Cargando..." : "Recargar"}
          </button>

          {mailMsg && (
            <div
              style={{
                fontSize: 12,
                color: "var(--muted)",
              }}
            >
              {mailMsg}
            </div>
          )}
        </div>

        <div
          style={{
            fontSize: 12,
            color: "var(--muted)",
            marginTop: 6,
          }}
        >
          * Campos obligatorios. Estos datos se usan por el endpoint{" "}
          <code>/mail/start</code> para envíos masivos.
        </div>
      </section>
    </>
  );
}
